import { editProfile, editUser, getUserByToken } from 'app/crud/auth.crud';
import { removeAllStorage } from 'app/utils/utils';
import { persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import { put, takeLatest } from 'redux-saga/effects';
import { PERSIST_AUTH, REDUCER_AUTH } from '../conf';

export const actionTypes = {
  Login: 'auth/LOGIN',
  LoginSSO: 'auth/LOGIN_SSO',
  Logout: 'auth/LOGOUT',
  Register: 'auth/REGISTER',
  UserRequested: 'auth/REQUEST_USER',
  UserLoaded: 'auth/USER_LOADED',
  UserEdit: 'auth/EDIT_USER',
  UserEditReducer: 'auth/EDIT_USER_REDUCER',
  UserEditProfile: 'auth/EDIT_PROFILE',
  UserEditProfileSuccess: 'auth/EDIT_PROFILE_SUCCESS',
  UserEditProfileError: 'auth/EDIT_PROFILE_ERROR',
  EditAvatar: 'auth/EDIT_AVATAR',
  ResetError: 'auth/RESET_ERROR',
  UpdateLicense: 'auth/UPDATE_LICENSE',
  SetUser: 'auth/SET_USER',
  UpdateHasLicenseMint: 'auth/UPDATE_HAS_LICENSE_MINT',
};
const initialAuthState = {
  user: undefined,
  authToken: undefined,
  license: null,
  error: null,
  oauth_accounts: [],
};

export const reducer = persistReducer(
  { storage, key: PERSIST_AUTH, whitelist: ['user', 'authToken', 'license', 'oauth_accounts'] },
  (state = initialAuthState, action) => {
    switch (action.type) {
      case actionTypes.Login:
      case actionTypes.LoginSSO: {
        const { authToken } = action.payload;

        return { authToken: authToken.token, oauth_accounts: authToken.oauth_accounts, user: undefined, license: authToken.license };
      }

      case actionTypes.Register: {
        const { authToken } = action.payload;

        return { authToken, user: undefined };
      }

      case actionTypes.Logout: {
        removeAllStorage();
        return initialAuthState;
      }

      case actionTypes.UserLoaded: {
        const { user } = action.payload;

        return { ...state, user: user.data };
      }

      case actionTypes.UserEditReducer: {
        const { user } = action.payload;

        return { ...state, user: { ...state.user, email: user.email, name: user.name, lastname: user.lastname } };
      }
      case actionTypes.UserEditProfile: {
        const { user } = action.payload;
        return { ...state, user: { ...state.user, ...user }, error: null };
      }

      case actionTypes.UserEditProfileSuccess: {
        const { user } = action.payload;
        return { ...state, user: { ...state.user, ...user }, error: false };
      }

      case actionTypes.UserEditProfileError: {
        const { user } = action.payload;
        return { ...state, user: { ...state.user, ...user }, error: false };
      }

      case actionTypes.EditAvatar: {
        const { avatar } = action.payload;
        return { ...state, user: { ...state.user, avatar }, error: null };
      }

      case actionTypes.ResetError: {
        return { ...state, error: null };
      }

      case actionTypes.UpdateLicense: {
        const license = action.payload;
        return { ...state, license: license };
      }

      case actionTypes.SetUser: {
        return { ...state, user: { ...state.user, ...action.payload } };
      }

      case actionTypes.UpdateHasLicenseMint: {
        const { has_license_mint } = action.payload;
        return { ...state, user: { ...state.user, has_license_mint } };
      }

      default:
        return state;
    }
  }
);

export const selectors = {
  getUser: (state) => {
    return state[REDUCER_AUTH] ? state[REDUCER_AUTH].user : null;
  },
  getUserGuid: (state) => {
    return state[REDUCER_AUTH]?.user?.guid ?? null;
  },
  getUserRoleGuid: (state) => {
    const user = selectors.getUser(state);
    return user ? user.role_guid : null;
  },
  getAuthToken: (state) => {
    return state[REDUCER_AUTH] ? state[REDUCER_AUTH].authToken : null;
  },
  getError: (state) => {
    return state[REDUCER_AUTH] ? state[REDUCER_AUTH].error : null;
  },
  getLicense: (state) => {
    return state[REDUCER_AUTH] ? state[REDUCER_AUTH].license : null;
  },
  getOauthAccounts: (state) => {
    return state[REDUCER_AUTH] ? state[REDUCER_AUTH].oauth_accounts : [];
  },
  getHasLicenseMint: (state) => {
    const user = selectors.getUser(state);
    return user ? user.has_license_mint : null;
  },
};

export const actions = {
  login: (authToken) => ({ type: actionTypes.Login, payload: { authToken } }),
  register: (authToken) => ({
    type: actionTypes.Register,
    payload: { authToken },
  }),
  /**
   * Accion de logout
   * @param reducersNoEmpty Listado de reducer que cuando escuchen la accion de  actionTypes.Logout no queremos que vuelvan a su estado inicial
   * // TODO: en un futuro preparar mas ficheros .duck que escuchan la accion actionTypes.Logout para que si viene en el array de reducersNoEmpty su reducer no lo limpie. Ahora solo lo he adaptado para los necesarios hasta ahora.
   */
  logout: (reducersNoEmpty = []) => ({ type: actionTypes.Logout, payload: { reducersNoEmpty } }),
  requestUser: (user) => ({ type: actionTypes.UserRequested, payload: { user } }),
  fulfillUser: (user) => ({ type: actionTypes.UserLoaded, payload: { user } }),
  editUser: (user) => ({ type: actionTypes.UserEdit, payload: { user } }),
  editUserReducer: (user) => ({ type: actionTypes.UserEditReducer, payload: { user } }),
  editProfile: (user) => ({ type: actionTypes.UserEditProfile, payload: { user } }),
  editProfileSuccess: (user) => ({ type: actionTypes.UserEditProfileSuccess, payload: { user } }),
  editProfileError: (user) => ({ type: actionTypes.UserEditProfileError, payload: {} }),
  editAvatar: (avatar) => ({ type: actionTypes.EditAvatar, payload: { avatar } }),
  resetError: () => ({ type: actionTypes.ResetError, payload: {} }),
  updateLicense: (license) => ({ type: actionTypes.UpdateLicense, payload: license }),
  setUser: (user) => ({ type: actionTypes.SetUser, payload: user }),
  updateHasLicenseMint: (has_license_mint) => ({ type: actionTypes.UpdateHasLicenseMint, payload: { has_license_mint } }),
};

export function* saga() {
  yield takeLatest(actionTypes.Login, function* loginSaga() {
    yield put(actions.requestUser());
  });

  yield takeLatest(actionTypes.LoginSSO, function* loginSSOSaga() {
    yield put(actions.requestUser());
  });

  yield takeLatest(actionTypes.Register, function* registerSaga() {
    yield put(actions.requestUser());
  });

  yield takeLatest(actionTypes.UserRequested, function* userRequested() {
    const { data: user } = yield getUserByToken();

    yield put(actions.fulfillUser(user));
  });

  yield takeLatest(actionTypes.UserEdit, function* userEdit(action) {
    const {
      data: { data: user },
    } = yield editUser(action.payload.user.guid, action.payload.user.name, action.payload.user.lastname, action.payload.user.email);
    yield put(actions.editUserReducer(user));
  });

  yield takeLatest(actionTypes.UserEditProfile, function* UserEditProfile(action) {
    try {
      const {
        data: { data: user },
      } = yield editProfile(action.payload.user);
      yield put(actions.editProfileSuccess(user));
    } catch (e) {
      yield put(actions.editProfileError());
    }
  });

  yield takeLatest(actionTypes.EditAvatar, function* userEdit(action) {
    yield put(actions.editProfileSuccess({}));
  });
}
